<script lang="ts" setup>
import "gitart-vue-dialog/dist/style.css";
import VehicleAutoComplete from "@/components/CvrConvert/UploadRequestVehicleAutoComplete.vue";
import SelectTime from "@/components/CvrConvert/SelectTime.vue";
import SelectRangeSelect from "@/components/CvrConvert/SelectRangeSelect.vue";
import CvrConvert from "@/services/models/CvrConvert";
import { GDialog } from "gitart-vue-dialog";
import { Ref, ref, reactive, defineProps, computed } from "vue";
import { formatDateTime } from "@/services/functions";
import RequestCameraType from "@/components/CvrConvert/RequestCameraType.vue"
import i18n from '@/lang/i18n';
import getVehicleResponse from "@/types/responses/getVehicle"
import LoadingAnimation from "@/components/CvrConvert/LoadingAnimation.vue"
import { CVR_UPLOAD_REQUEST_SELECT_RANGE } from "@/services/consts"
import getVehicleType from "@/types/responses/getVehicle"
import { scrollToErrorComponent } from "@/services/validators"

let requesting = ref(false);
let showableButtonArea = ref(true);

const getRange = ref(CVR_UPLOAD_REQUEST_SELECT_RANGE[0])
const isDisabledRequestButton: Ref<boolean> = ref(false)
const loading: Ref<boolean> = ref(false);
const isSucceeded: Ref<boolean> = ref(false)

const emits = defineEmits(['update:modelValue'])

const props = defineProps<{
  reload: () => void;
  showFlash: (message: string, type: string, fadeout: boolean) => {value: any};
  modelValue: boolean
  reading: boolean
  ranges: { start_at: Date, end_at: Date }[]
  selectedVehicle: getVehicleResponse
  createAuthority: boolean
}>();

type errorTypes = {
  start_datetime: string[],
  end_datetime: string[],
  vehicle: string[],
  files: string[],
  types: string[],
}

type formType = {
  start_datetime: string;
  end_datetime: string;
  vehicle: getVehicleType | undefined;
  files: File[];
  types: string[];
}

const form = reactive({
  start_datetime: "",
  end_datetime: "",
  vehicle: props.selectedVehicle,
  files: [],
  types: [] as string[]
}) as formType;

const modelValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emits('update:modelValue', value);
  },
})

const initializeForm = () => {
  form.start_datetime = "";
  form.end_datetime = "";
  form.files = [];
  form.types = [];
  getRange.value = CVR_UPLOAD_REQUEST_SELECT_RANGE[0];
};

const errors = reactive({
  start_datetime: [],
  end_datetime: [],
  vehicle: [],
  files: [],
  types: [],
}) as errorTypes;

const initializeErrors = () => {
  errors.start_datetime = [];
  errors.end_datetime = [];
  errors.vehicle = [];
  errors.files = [];
  errors.types = [];
};

const endDateTime = computed(() => {
  if (!form.start_datetime) return ""
  const selectedPeriod = CVR_UPLOAD_REQUEST_SELECT_RANGE.indexOf(getRange.value) + 1
  const startTime = new Date(form.start_datetime)
  const endTime = new Date(startTime.setMinutes(startTime.getMinutes() + selectedPeriod))
  return formatDateTime(endTime);
})

const selectedPeriod = computed(() => {
  if (!form.start_datetime) return ""
  return `${form.start_datetime} ~ ${endDateTime.value}`
})

const moveFormView = () => {
  if (!validate()) return;
  requesting.value = true;

  CvrConvert.request({
    start_datetime: form.start_datetime,
    end_datetime: endDateTime.value,
    device_id: props.selectedVehicle.digital_tacograph,
    types: form.types,
    files: [],
  })
    .then(() => {
      props.showFlash(i18n.global.t('pages.cvr_uploads.requestPanel.success'), "success", true);
      isSucceeded.value = true
      closeModal()
    })
    .catch((error) => {
      if (error?.response?.data?.errors) {
        setErrors(error.response.data.errors)
      } else {
        closeModal()
        props.showFlash(i18n.global.t('pages.cvr_uploads.requestPanel.failed'), "error", false);
      }
    })
    .finally(() => {
      requesting.value = false;
    });
};

const validate = () => {
  let isValid = true;
  initializeErrors();
  if (!validateStartDateTime()) isValid = false;
  if (!validateVehicle()) isValid = false;

  if (!isValid) scrollToErrorComponent()
  return isValid;
};

const validateVehicle = () => {
  if (!props.selectedVehicle.id) {
    errors.vehicle.push(i18n.global.t('pages.cvr_uploads.errorMessages.notSelect'));
  } else if (!props.selectedVehicle.digital_tacograph) {
    errors.vehicle.push(i18n.global.t('pages.cvr_uploads.errorMessages.hasNoDevice'));
  } else {
    return !errors.vehicle[0];
  }
};

const validateStartDateTime = () => {
  if (!form.start_datetime) {
    errors.start_datetime.push(i18n.global.t('pages.cvr_uploads.errorMessages.notSelectStartDateTime'));
    return false;
  }
  return true;
};

const setErrors = (responseErrors: {[key:string]: string[]}) => {
  errors.start_datetime = responseErrors?.start_datetime ?? [];
  errors.end_datetime = responseErrors?.end_datetime ?? [];
  errors.vehicle = responseErrors?.device_id ?? [];
  errors.types = responseErrors?.types ?? [];
  scrollToErrorComponent()
}

const changeStatusRequestButton = (bool: boolean) => {
  isDisabledRequestButton.value = bool;
}

const closeModal = () => {
  if (isSucceeded.value) props.reload()
  modelValue.value = false
  initializeForm();
  initializeErrors();
}
</script>

<template>
  <template v-if="modelValue">
    <GDialog
      ref="spot_modal"
      v-model="modelValue"
      max-width="50%"
      transition="custom-from-bottom-transition"
      persistent
    >
      <div class="row m-0 w-100 upload-panel v-theme--light">
        <div class="row m-0 p-0 w-100 requesting" v-if="requesting">
          <div class="d-flex justify-center m-0 text-white">{{ i18n.global.t('pages.cvr_uploads.requestPanel.requesting') }}</div>
        </div>
        <div class="row m-0 p-0 w-100 requesting" v-if="props.reading">
          <div class="d-flex justify-center m-0 text-white">{{ i18n.global.t('pages.cvr_uploads.requestPanel.loading') }}</div>
        </div>
        <div class="row m-0 pb-0 notice">
          <div>{{ i18n.global.t('pages.cvr_uploads.notice') }}</div>
        </div>
        <div v-if="props.selectedVehicle" class="row m-0">
          <div class="col-9">
            <v-text-field
              :label="props.selectedVehicle.number_plate"
              readonly
              :error-messages="errors.vehicle"
            />
          </div>
        </div>
        <div v-else class="row m-0">
          <div class="col-9">
            <VehicleAutoComplete
              v-model="form.vehicle"
              :requestParams="{ type: 'filter' }"
              :error-messages="errors.vehicle"
            />
          </div>
        </div>
        <div style="position: relative;">
          <div class="row m-0">
            <SelectTime
              :label="i18n.global.t('pages.cvr_uploads.startDateTime')"
              v-model="form.start_datetime"
              :loading="loading"
              :reading="props.reading"
              :ranges="props.ranges"
              :errors="errors.start_datetime"
            />
          </div>
          <div class="row m-0 pt-0">
            <div class="col-6">
              <SelectRangeSelect
                :ranges="props.ranges"
                :selectedTime="form.start_datetime"
                v-model="getRange"
              />
            </div>
          </div>
          <div class="row m-0 pt-0">
            <p class="col-9 mb-0">
              {{ `${i18n.global.t('pages.cvr_uploads.selectedPeriod')}: ${selectedPeriod}` }}
            </p>
          </div>
          <div class="row m-0 py-0">
            <RequestCameraType
              v-model="form.types"
              :reading="props.reading"
              :vehicle="props.selectedVehicle"
              :errors="errors.types"
              @changeStatusRequestButton="changeStatusRequestButton"
            />
          </div>
        </div>
        <LoadingAnimation
          :loading="loading"
        />
        <div class="row m-0 v-theme--light justify-content-end">
          <div style="width: fit-content" v-if="showableButtonArea">
            <v-btn class="reset-btn" @click="closeModal">
              {{ i18n.global.t('common.cancelBtn') }}
            </v-btn>
          </div>
          <div style="width: fit-content" v-if="showableButtonArea">
            <v-btn
              v-if="props.createAuthority"
              :loading="loading"
              :disabled="isDisabledRequestButton"
              class="exec-btn"
              @click="moveFormView"
              :style="{
                backgroundColor : isDisabledRequestButton ? '#B3B8BB' : ''
              }"
            >
              {{ i18n.global.t('pages.cvr_uploads.requestPanel.requestBtn') }}
            </v-btn>
          </div>
        </div>
      </div>
    </GDialog>
  </template>
</template>

<style scoped>
.upload-panel {
  position: relative;
}
.requesting {
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  background-color: rgba(150, 150, 150, 0.7);
  z-index: 1000;
}
.requesting > div {
  position: absolute;
  top: 50%;
}
.error .dp__input {
  border: 1px solid rgb(var(--v-theme-error));
}
.error *,
.v-input--error .v-messages__message {
  color: rgb(var(--v-theme-error));
  opacity: 1;
}
.attachment-error {
  padding-left: 40px;
}
.notice > div{
  color: red;
  font-weight: bold;
  white-space: pre-wrap;
}
</style>
